import { Box } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Timeline from '@material-ui/lab/Timeline';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import React, { useEffect } from 'react';
import { IBundlePackage, IStory } from '../../services/types';
import SingleBundle from './SingleBundle';

interface IProps {
  story: IStory;
  bundles: IBundlePackage[];
  title?: string;
  isEditing?: boolean;
  removable?: boolean;
  onChange: (bundles: IBundlePackage[]) => void;
  onRemoveClick?: (bundle: IBundlePackage) => void;
  onValidate?: (validationMessages: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    maxWidth: 660,
  },
  timelineRoot: {},
  missingOppositeContent: {
    '&::before': {
      content: 'abbs !important',
    },
  },
  oppositeContent: {
    display: 'none',
  },
  hide: {
    visibility: 'hidden',
  },
}));

export const getInitialBundle = (partialBundle: Partial<IBundlePackage> = {}): IBundlePackage => {
  return Object.assign({}, partialBundle, {
    storyId: 0,
    story: undefined,
    targetType: 'default',
    showRemainAll: false,
    prices: [
      { price: 0, episodeNum: 0 },
      { price: 0, episodeNum: 0 },
      { price: 0, episodeNum: 0 },
      { price: 0, episodeNum: 0 },
    ],
  });
};

const BundlePackageItem = (props: IProps): JSX.Element => {
  const { story, title = '', bundles, isEditing = false, removable = false, onChange, onRemoveClick = () => {}, onValidate = () => {} } = props;
  const classes = useStyles();

  const [basicBundle, ...dynamicBundles] = bundles;

  const handleAddDynamicBundleClick = () => {
    const newBundles = [...bundles, { ...getInitialBundle(), storyId: story.id }];
    onChange(newBundles);
  };

  useEffect(() => {
    const messageSet = new Set<string>();

    if (!story) {
      messageSet.add('Select a story');
    }

    bundles.forEach((bundle, index) => {
      if (!bundle.prices.find(p => p.episodeNum !== 0 && p.price !== 0)) {
        messageSet.add('At least one price must be selected.');
      }

      if (!!bundle.prices.find(price => price.episodeNum > 0 && !price.price)) {
        messageSet.add('Coins cannot be empty.');
      }

      const isDynamicBundle = index > 0;

      if (isDynamicBundle) {
        if (!bundle.minRemainEpisodesNumber || !bundle.maxRemainEpisodesNumber) {
          messageSet.add('the remain episodes count cannot be empty.');
        }

        if (bundle.minRemainEpisodesNumber && bundle.minRemainEpisodesNumber <= 0) {
          messageSet.add('the remain episodes count should be a positive value.');
        }

        if (bundle.maxRemainEpisodesNumber && bundle.maxRemainEpisodesNumber <= 0) {
          messageSet.add('the remain episodes count should be a positive value.');
        }

        if (bundle.minRemainEpisodesNumber && bundle.maxRemainEpisodesNumber && bundle.minRemainEpisodesNumber > bundle.maxRemainEpisodesNumber) {
          messageSet.add('the minimum count exceed the maximum count.');
        }

        const isEpisodeRangeOverlap = bundles.slice(1).reduce((acc, cur, i) => {
          const isSameBundle = index - 1 === i;

          if (isSameBundle) {
            return acc || false;
          }

          if (!(bundle.minRemainEpisodesNumber && bundle.maxRemainEpisodesNumber && cur.minRemainEpisodesNumber && cur.maxRemainEpisodesNumber)) {
            return acc || false;
          }

          if (bundle.minRemainEpisodesNumber > cur.maxRemainEpisodesNumber) {
            return acc || false;
          }

          if (bundle.maxRemainEpisodesNumber < cur.minRemainEpisodesNumber) {
            return acc || false;
          }

          return acc || true;
        }, false);

        if (isEpisodeRangeOverlap) {
          messageSet.add('The remaining episodes ranges overlap.');
        }
      }
    });

    onValidate(Array.from(messageSet));
  }, [bundles, story, onValidate]);

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      {basicBundle && (
        <SingleBundle
          story={story}
          bundle={basicBundle}
          isDynamicBundle={false}
          isEditing={isEditing}
          removable={removable}
          onChange={(b: IBundlePackage) => {
            const newBasicBundle = Object.assign({}, basicBundle, b);
            onChange([newBasicBundle, ...dynamicBundles]);
          }}
          title={title}
          onRemoveClick={onRemoveClick}
          onAddDynamicBundleClick={handleAddDynamicBundleClick}
        />
      )}

      {dynamicBundles && dynamicBundles.length > 0 && (
        <Box>
          <Timeline align="left">
            {dynamicBundles.map((bundle, i) => (
              <TimelineItem key={`${bundle.id}-${bundle.storyId}-${i}`}>
                <TimelineOppositeContent className={classes.oppositeContent}></TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineConnector />
                  <TimelineDot />
                  <TimelineConnector className={dynamicBundles.length <= i + 1 ? classes.hide : undefined} />
                </TimelineSeparator>
                <TimelineContent>
                  <SingleBundle
                    isDynamicBundle={true}
                    story={story}
                    bundle={bundle}
                    onChange={(b: IBundlePackage) => {
                      const newDynamicBundles = [...dynamicBundles];
                      newDynamicBundles[i] = Object.assign({}, newDynamicBundles[i], b);
                      onChange([{ ...basicBundle }, ...newDynamicBundles]);
                    }}
                    isEditing={isEditing}
                    removable={isEditing}
                    title={title}
                    onRemoveClick={() => {
                      const newDynamicBundles = [...dynamicBundles];
                      newDynamicBundles.splice(i, 1);

                      onChange([{ ...basicBundle }, ...newDynamicBundles]);
                    }}
                  />
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Box>
      )}
    </Box>
  );
};

export default BundlePackageItem;
